'use client';

import { Block, BLOCKS, Document, TopLevelBlock } from '@contentful/rich-text-types';
import { documentToReactComponents, Options } from '@contentful/rich-text-react-renderer';
import { cn } from 'utils/styles';

/**
 * Extracts the plain text content from a table cell block.
 */
function getCellText(cell: Block): string {
  const parts: string[] = [];

  function walk(node: { nodeType: string; value?: string; content?: unknown[] }) {
    if (node.nodeType === 'text' && typeof node.value === 'string') {
      parts.push(node.value);
    }
    if (Array.isArray(node.content)) {
      for (const child of node.content) {
        walk(child as { nodeType: string; value?: string; content?: unknown[] });
      }
    }
  }

  walk(cell);
  return parts.join('').trim();
}

/**
 * Renders the rich text content of a table cell using documentToReactComponents.
 */
function renderCellContent(cell: Block, options?: Options): React.ReactNode {
  const doc: Document = {
    nodeType: BLOCKS.DOCUMENT,
    data: {},
    content: (cell.content ?? []) as TopLevelBlock[],
  };
  return documentToReactComponents(doc, options);
}

interface MergedCell {
  cell: Block;
  colspan: number;
}

/**
 * Processes a row's cells, merging any cell whose text is exactly "^" into the
 * preceding cell by incrementing its colspan.
 */
function mergeRowCells(cells: Block[]): MergedCell[] {
  const merged: MergedCell[] = [];

  for (const cell of cells) {
    if (getCellText(cell) === '^' && merged.length > 0) {
      merged[merged.length - 1].colspan += 1;
    } else {
      merged.push({ cell, colspan: 1 });
    }
  }

  return merged;
}

interface AgreementTableProps {
  node: Block;
  renderOptions?: Options;
}

/**
 * Desktop table view. Renders inner borders only (between rows and columns,
 * no outer border) with colspan merging via `^`.
 */
function DesktopTable({
  rows,
  renderOptions,
  hiddenOnMobile,
  noMargin,
}: {
  rows: Block[];
  renderOptions?: Options;
  hiddenOnMobile?: boolean;
  noMargin?: boolean;
}) {
  const isHeaderRow = (row: Block) => {
    const cells = (row.content ?? []) as Block[];
    return cells.length > 0 && cells[0].nodeType === 'table-header-cell';
  };

  return (
    <table
      className={cn(
        'w-full table-fixed text-left border-collapse font-sans',
        !noMargin && 'my-12',
        hiddenOnMobile && 'hidden md:table',
      )}>
      <tbody>
        {rows.map((row, rowIndex) => {
          const cells = (row.content ?? []) as Block[];
          const mergedCells = mergeRowCells(cells);
          const isHeader = isHeaderRow(row);
          const isLastRow = rowIndex === rows.length - 1;

          return (
            <tr
              key={rowIndex}
              className={cn(isHeader ? 'text-kale' : 'text-grey-200', !isLastRow && 'border-b border-pebble')}>
              {mergedCells.map((merged, cellIndex) => {
                const content = renderCellContent(merged.cell, renderOptions);
                const isTh = merged.cell.nodeType === 'table-header-cell';
                const colSpan = merged.colspan > 1 ? merged.colspan : undefined;
                const isLastCell = cellIndex === mergedCells.length - 1;
                const cellBorder = !isLastCell ? 'border-r border-pebble' : '';

                if (isTh) {
                  return (
                    <th key={cellIndex} colSpan={colSpan} className={`font-medium px-4 py-6 align-top ${cellBorder}`}>
                      {content}
                    </th>
                  );
                }

                return (
                  <td key={cellIndex} colSpan={colSpan} className={`px-4 py-6 align-top ${cellBorder}`}>
                    {content}
                  </td>
                );
              })}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

/**
 * Returns true if a row contains any `^` merge markers.
 */
function rowHasMerge(row: Block): boolean {
  const cells = (row.content ?? []) as Block[];
  return cells.some(cell => getCellText(cell) === '^');
}

/**
 * Mobile transposed table. Each data row's cells are paired with their
 * column header in a two-column layout.
 *
 * Desktop:                    Mobile:
 * Header A | Header B         Header A | copy1
 * copy1    | copy2            Header B | copy2
 * copy3    | copy4            Header A | copy3
 *                             Header B | copy4
 */
function MobileTransposedTable({
  headerCells,
  dataRows,
  renderOptions,
  isLastSection,
}: {
  headerCells: Block[];
  dataRows: Block[];
  renderOptions?: Options;
  isLastSection: boolean;
}) {
  return (
    <table className="w-full text-left border-collapse font-sans">
      <tbody>
        {dataRows.map((row, rowIndex) => {
          const cells = (row.content ?? []) as Block[];
          const isLastRow = isLastSection && rowIndex === dataRows.length - 1;

          return cells.map((cell, cellIndex) => {
            const header = headerCells[cellIndex];
            const isLastCell = cellIndex === cells.length - 1;
            const showBorder = !(isLastRow && isLastCell);

            return (
              <tr key={`${rowIndex}-${cellIndex}`} className={showBorder ? 'border-b border-pebble' : ''}>
                <th className="w-1/2 text-kale font-medium px-4 py-6 align-top border-r border-pebble">
                  {header ? renderCellContent(header, renderOptions) : null}
                </th>
                <td className="w-1/2 text-grey-200 px-4 py-6 align-top">{renderCellContent(cell, renderOptions)}</td>
              </tr>
            );
          });
        })}
      </tbody>
    </table>
  );
}

/**
 * Table component for agreement pages. Renders a plain HTML table with support
 * for colspan merging via the `^` marker convention. When a cell contains only
 * the text "^", it is merged into the cell to its left.
 *
 * On mobile, tables with an unmerged header row transpose: each data cell is
 * paired with its column header. Transposition stops at the first data row
 * that contains merge markers — that row and all subsequent rows render as
 * a normal (non-transposed) table.
 */
const AgreementTable = ({ node, renderOptions }: AgreementTableProps) => {
  const rows = (node.content ?? []) as Block[];

  const hasHeaderRow = rows.length > 0 && ((rows[0].content ?? []) as Block[])[0]?.nodeType === 'table-header-cell';
  const headerHasMerge = hasHeaderRow && rowHasMerge(rows[0]);

  // Only transpose if there's an unmerged header row with data rows
  if (!hasHeaderRow || headerHasMerge || rows.length <= 1) {
    return <DesktopTable rows={rows} renderOptions={renderOptions} />;
  }

  // Split data rows into transposable (no merges) and remaining (from first merge onward)
  const dataRows = rows.slice(1);
  let splitIndex = dataRows.length;
  for (let i = 0; i < dataRows.length; i++) {
    if (rowHasMerge(dataRows[i])) {
      splitIndex = i;
      break;
    }
  }

  const transposableRows = dataRows.slice(0, splitIndex);
  const remainingRows = dataRows.slice(splitIndex);
  const headerCells = (rows[0].content ?? []) as Block[];

  return (
    <>
      <DesktopTable rows={rows} renderOptions={renderOptions} hiddenOnMobile />
      {(transposableRows.length > 0 || remainingRows.length > 0) && (
        <div className="my-12 md:hidden">
          {transposableRows.length > 0 && (
            <MobileTransposedTable
              headerCells={headerCells}
              dataRows={transposableRows}
              renderOptions={renderOptions}
              isLastSection={remainingRows.length === 0}
            />
          )}
          {remainingRows.length > 0 && <DesktopTable rows={remainingRows} renderOptions={renderOptions} noMargin />}
        </div>
      )}
    </>
  );
};

export { AgreementTable };
